import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock3, X } from "lucide-react";
import { setRateLimitHandler } from "@/lib/api-client";

interface RateLimitToastState {
  id: string;
  requestPath: string;
  retryAfterSeconds: number;
  expiresAt: number;
}

function formatRequestLabel(requestPath: string): string {
  if (requestPath.startsWith("/crm/ai") || requestPath.startsWith("/v1/ai")) {
    return "AI";
  }

  if (requestPath.startsWith("/crm/email") || requestPath.startsWith("/v1/email")) {
    return "email";
  }

  if (requestPath.startsWith("/crm/reviews") || requestPath.startsWith("/v1/reviews")) {
    return "reviews";
  }

  return "this action";
}

export function RateLimitToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<RateLimitToastState | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    setRateLimitHandler(({ retryAfterSeconds, requestPath }) => {
      const safeRetryAfterSeconds = Math.max(1, retryAfterSeconds);
      setToast({
        id: crypto.randomUUID(),
        requestPath,
        retryAfterSeconds: safeRetryAfterSeconds,
        expiresAt: Date.now() + safeRetryAfterSeconds * 1000,
      });
      setRemainingSeconds(safeRetryAfterSeconds);
    });

    return () => {
      setRateLimitHandler(null);
    };
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const tick = () => {
      const nextSeconds = Math.max(0, Math.ceil((toast.expiresAt - Date.now()) / 1000));
      setRemainingSeconds(nextSeconds);
      if (nextSeconds <= 0) {
        setToast(null);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [toast]);

  const progressPercent = useMemo(() => {
    if (!toast) {
      return 0;
    }

    return Math.max(0, Math.min(100, (remainingSeconds / toast.retryAfterSeconds) * 100));
  }, [remainingSeconds, toast]);

  return (
    <>
      {children}
      {toast ? (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="pointer-events-none fixed bottom-6 left-1/2 z-[90] -translate-x-1/2"
        >
          <div className="pointer-events-auto min-w-[360px] overflow-hidden rounded-xl border border-amber-500/35 bg-amber-50 text-amber-950 shadow-2xl shadow-amber-900/10">
            <div className="flex items-start gap-3 px-4 py-3">
              <div className="mt-0.5 rounded-full bg-amber-500/15 p-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Too many requests</p>
                <p className="mt-1 text-sm">
                  {`Too many requests for ${formatRequestLabel(toast.requestPath)}. Try again in ${remainingSeconds} second${remainingSeconds === 1 ? "" : "s"}.`}
                </p>
                <div className="mt-2 flex items-center gap-1 text-xs text-amber-800/80">
                  <Clock3 className="h-3.5 w-3.5" />
                  <span>Rate limit countdown is based on the server&apos;s `Retry-After` header.</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setToast(null)}
                className="rounded-md p-1 text-amber-800/70 transition-colors hover:bg-amber-500/10 hover:text-amber-950"
                aria-label="Dismiss rate limit toast"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="h-1 bg-amber-900/10">
              <div
                className="h-full bg-amber-500 transition-all duration-1000 ease-linear"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
